"use client";

import { useEffect, useMemo, useState } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { KEY_ENTER_COMMAND, COMMAND_PRIORITY_HIGH } from "lexical";
import { LuSparkles, LuMapPin, LuBox } from "react-icons/lu";
import { apiFetch } from "@/lib/api";
import { useShow } from "@/contexts/ShowContext";
import { useUser } from "@/contexts/UserContext";
import { MentionNode } from "./MentionNode";
import { MentionChipContext } from "./MentionComponent";
import ArtifactMentionPlugin from "./ArtifactMentionPlugin";
import SerializationPlugin from "./SerializationPlugin";
import InitializationPlugin from "./InitializationPlugin";
import MentionDeletionPlugin from "./MentionDeletionPlugin";
import AssetMentionPlugin from "./AssetMentionPlugin";
import { EditorAssetsProvider, useEditorAssets } from "./EditorAssetsContext";
import {
  characterArtifactItems,
  locationArtifactItems,
  propArtifactItems,
  nonCharacterArtifactItems,
} from "./artifactMentionItems";

const EDITOR_THEME = {
  paragraph: "editor-paragraph",
  text: {
    bold: "editor-text-bold",
    italic: "editor-text-italic",
    underline: "editor-text-underline",
  },
};

// Fires `onSubmit` on Ctrl/Cmd+Enter (plain Enter still inserts a newline).
function EnterSubmitPlugin({ onSubmit }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (!onSubmit) return;
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (event && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          onSubmit();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor, onSubmit]);
  return null;
}

const ASSET_ID_IN_NOTATION = /"assetId"\s*:\s*"([a-f0-9]{24})"/g;

const assetIdsInNotation = (notation) =>
  [...String(notation || "").matchAll(ASSET_ID_IN_NOTATION)].map(
    (match) => match[1],
  );

// Chips seeded from `value` carry only an id, so the records behind them are
// fetched once up front rather than one request per chip as each mounts.
function AssetHydrationPlugin({ value }) {
  const { ensureAssets } = useEditorAssets();

  useEffect(() => {
    const assetIds = assetIdsInNotation(value);
    if (assetIds.length === 0) return;
    ensureAssets(assetIds);
  }, [value, ensureAssets]);

  return null;
}

/**
 * Rich prompt input built on the shared Lexical mention stack:
 *  - `#` mentions saved `character`-scope prompts, inserted inline
 *  - `@` mentions saved `location`-scope prompts, inserted inline
 *  - `&` mentions saved `prop`-scope prompts, inserted inline
 *  - `/` mentions every other saved prompt scope, inline
 *
 * Value in/out is the same notation string the script editor uses: plain text
 * with mentions inlined as `{…json}` blocks (see SerializationPlugin). Mentions
 * are stored as-is for now; resolving them to plain text is left to the caller.
 *
 * The editor seeds from `value` once per mount; pass a changing `resetKey`
 * (e.g. the selected model id) to force a re-seed when the context changes.
 *
 * Bump `promptsVersion` after saving a prompt to refetch the mention lists, so
 * a just-saved prompt is mentionable without a remount.
 */
export default function ArtifactRichInput({
  value = "",
  onChange,
  onSubmit,
  placeholder = "Type your prompt here & let's create something amazing…  (# character, @ location, & prop, / artifact, drag and drop assets directly)",
  className = "",
  resetKey = "default",
  promptsVersion = 0,
  resolveAsset,
  onAssetsDropped,
  onLoadArtifactMention,
}) {
  const { user } = useUser();
  const { show } = useShow();
  const [prompts, setPrompts] = useState([]);

  useEffect(() => {
    if (!show?._id) return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await apiFetch(`/api/shows/${show._id}/artifacts`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setPrompts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching prompts:", err);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [promptsVersion, show?._id]);

  const characterItems = useMemo(
    () => characterArtifactItems(prompts, user?.id),
    [prompts, user?.id],
  );

  const locationItems = useMemo(
    () => locationArtifactItems(prompts, user?.id),
    [prompts, user?.id],
  );

  const propItems = useMemo(
    () => propArtifactItems(prompts, user?.id),
    [prompts, user?.id],
  );

  const promptItems = useMemo(
    () => nonCharacterArtifactItems(prompts, user?.id),
    [prompts, user?.id],
  );

  // Compact character chips + a resolver so double-clicking an artifact chip
  // can hand its full record up to the caller (see ArtifactMentionChip in
  // MentionComponent) — e.g. AiModelInput loads it into artifact-edit mode.
  const chipContext = useMemo(
    () => ({
      compactCharacter: true,
      loadArtifact: (id) => {
        const found = prompts.find((p) => p._id === id);
        if (found) onLoadArtifactMention?.(found);
      },
    }),
    [prompts, onLoadArtifactMention],
  );

  const initialConfig = {
    namespace: "ArtifactRichInput",
    theme: EDITOR_THEME,
    nodes: [MentionNode],
    onError: (error) => console.error("Lexical error:", error),
  };

  return (
    <div className={`relative w-full h-full min-h-0 ${className}`}>
      <LexicalComposer key={resetKey} initialConfig={initialConfig}>
        <EditorAssetsProvider>
          <MentionChipContext.Provider value={chipContext}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable className="pb-10 w-full h-full min-h-0 outline-hidden resize-none overflow-y-auto text-secondary-foreground text-xs/4 font-mono [&>p]:my-0.5" />
              }
              placeholder={
                <div className="absolute top-0 left-0 text-xs/4 font-mono text-secondary-foreground/30 pointer-events-none select-none">
                  {placeholder}
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            <InitializationPlugin initialValue={value} />
            <SerializationPlugin onChange={onChange} />
            <ArtifactMentionPlugin
              trigger="#"
              mentionType="artifact"
              items={characterItems}
              icon={<LuSparkles />}
              emptyMessage="No character artifacts found"
            />
            <ArtifactMentionPlugin
              trigger="@"
              mentionType="artifact"
              items={locationItems}
              icon={<LuMapPin />}
              emptyMessage="No location artifacts found"
            />
            <ArtifactMentionPlugin
              trigger="&"
              mentionType="artifact"
              items={propItems}
              icon={<LuBox />}
              emptyMessage="No prop artifacts found"
            />
            <ArtifactMentionPlugin
              trigger="/"
              mentionType="artifact"
              items={promptItems}
              icon={<LuSparkles />}
              emptyMessage="No artifacts found"
            />
            <AssetMentionPlugin
              resolveAsset={resolveAsset}
              onAssetsDropped={onAssetsDropped}
            />
            <MentionDeletionPlugin />
            <EnterSubmitPlugin onSubmit={onSubmit} />
            <AssetHydrationPlugin value={value} />
          </MentionChipContext.Provider>
        </EditorAssetsProvider>
      </LexicalComposer>
    </div>
  );
}
